/* Home page: shared behaviour ("Linen & Label").
   One IntersectionObserver for reveals, plus the philosophy tab strip, which
   answers to a sideways swipe as well as a tap.

   Every home section references this file. The browser executes it once (same
   src), and every binding is guarded by a data-*-bound attribute so re-running
   it is safe. It is re-run on shopify:section:load, because the theme editor
   re-renders a section's markup without re-executing an already-loaded script.
   Without that, a freshly added section's [data-hm-reveal] elements would
   stay at opacity 0 and its tabs would not bind.

   Mirrors assets/story-shared.js. All motion is gated behind
   prefers-reduced-motion.

   The values expander is a native <details>/<summary> and the carousels are
   CSS scroll-snap, so neither needs JS. */
(function () {
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initReveals(scope) {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var mobile = window.innerWidth <= 749;

    var els = scope.querySelectorAll('[data-hm-reveal]:not([data-hm-reveal-bound])');

    if (reduce) {
      els.forEach(function (el) {
        el.setAttribute('data-hm-reveal-bound', '');
        el.classList.add('is-visible');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          /* 80ms stagger between revealing siblings, capped so a long row
             never leaves the last card waiting. */
          var siblings = Array.prototype.filter.call(
            entry.target.parentNode.children,
            function (node) {
              return node.hasAttribute && node.hasAttribute('data-hm-reveal');
            }
          );
          var index = siblings.indexOf(entry.target);
          if (index > 0) {
            entry.target.style.transitionDelay = Math.min(index, 5) * 80 + 'ms';
          }

          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { threshold: mobile ? 0.12 : 0.15, rootMargin: '0px 0px -6% 0px' }
    );

    els.forEach(function (el) {
      el.setAttribute('data-hm-reveal-bound', '');
      io.observe(el);
    });
  }

  /* Tabs. The markup ships with every panel visible and the tab strip hidden;
     adding .is-tabbed here is what switches the section into one-at-a-time.
     If this script never runs, all three panels simply stay on the page. */
  function initTabs(scope) {
    var groups = scope.querySelectorAll('[data-hm-tabs]:not([data-hm-tabs-bound])');

    groups.forEach(function (group) {
      var tabs = Array.prototype.slice.call(group.querySelectorAll('[role="tab"]'));
      var panels = Array.prototype.slice.call(group.querySelectorAll('[role="tabpanel"]'));

      /* Nothing to switch between. Leave it as plain stacked content. */
      if (tabs.length < 2 || tabs.length !== panels.length) return;

      group.setAttribute('data-hm-tabs-bound', '');
      group.classList.add('is-tabbed');

      function select(index, moveFocus) {
        tabs.forEach(function (tab, i) {
          var on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
          panels[i].classList.toggle('is-active', on);
        });
        if (moveFocus) tabs[index].focus();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          select(i, false);
        });

        tab.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            next = (i + 1) % tabs.length;
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            next = (i - 1 + tabs.length) % tabs.length;
          } else if (event.key === 'Home') {
            next = 0;
          } else if (event.key === 'End') {
            next = tabs.length - 1;
          }
          if (next === null) return;
          event.preventDefault();
          select(next, true);
        });
      });

      /* Trust the markup's own starting state rather than assuming the first
         panel: a merchant may have reordered blocks. */
      var start = tabs.findIndex(function (tab) {
        return tab.getAttribute('aria-selected') === 'true';
      });
      select(start === -1 ? 0 : start, false);

      /* Swipe, because a user test had people dragging sideways as well as
         tapping. Vertical intent is left alone: nothing is prevented, and a
         gesture only counts if it is clearly sideways, so a finger that mostly
         moves down still scrolls the page. No wrapping at either end, so
         running out of panels feels like running out rather than looping. */
      var track = panels[0].parentNode;
      if (!track) return;

      var startX = 0;
      var startY = 0;
      var tracking = false;

      track.addEventListener(
        'touchstart',
        function (event) {
          tracking = event.touches.length === 1;
          if (!tracking) return;
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true }
      );

      track.addEventListener(
        'touchend',
        function (event) {
          if (!tracking) return;
          tracking = false;

          var touch = event.changedTouches[0];
          if (!touch) return;

          var dx = touch.clientX - startX;
          var dy = touch.clientY - startY;
          if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.4) return;

          var current = tabs.findIndex(function (tab) {
            return tab.getAttribute('aria-selected') === 'true';
          });
          if (current === -1) current = 0;

          var next = dx < 0 ? current + 1 : current - 1;
          if (next < 0 || next > tabs.length - 1) return;
          select(next, false);
        },
        { passive: true }
      );
    });
  }

  /* Numbered scroll nav (1, 2, 3): the mobile-only strip of number buttons
     under a horizontal scroller, e.g. the Collection cards. Mirrors
     certification-panels.js's dot indicator, generalized to any
     [data-hm-scrollnav] group instead of one fixed markup shape. */
  function initScrollNav(scope) {
    var groups = scope.querySelectorAll('[data-hm-scrollnav]:not([data-hm-scrollnav-bound])');

    groups.forEach(function (group) {
      group.setAttribute('data-hm-scrollnav-bound', '');

      var row = group.querySelector('[data-hm-scrollnav-row]');
      var buttons = Array.prototype.slice.call(group.querySelectorAll('[data-hm-scrollnav-item]'));
      var items = row ? Array.prototype.slice.call(row.children) : [];
      if (!row || buttons.length < 2 || buttons.length !== items.length) return;

      function gutter() {
        return parseFloat(window.getComputedStyle(row).paddingLeft) || 0;
      }

      function mark(index) {
        buttons.forEach(function (btn, i) {
          if (i === index) btn.setAttribute('aria-current', 'true');
          else btn.removeAttribute('aria-current');
        });
      }

      /* Nearest item to the scrollport's leading edge, matching how the
         items snap to start. */
      function nearest() {
        var left = row.scrollLeft + gutter();
        var best = 0;
        var bestGap = Infinity;
        items.forEach(function (item, i) {
          var gap = Math.abs(item.offsetLeft - left);
          if (gap < bestGap) {
            bestGap = gap;
            best = i;
          }
        });
        return best;
      }

      var queued = false;
      row.addEventListener(
        'scroll',
        function () {
          if (queued) return;
          queued = true;
          window.requestAnimationFrame(function () {
            queued = false;
            mark(nearest());
          });
        },
        { passive: true }
      );

      buttons.forEach(function (btn, i) {
        btn.addEventListener('click', function () {
          var target = items[i];
          if (!target) return;
          var delta =
            target.getBoundingClientRect().left - row.getBoundingClientRect().left - gutter();
          row.scrollTo({
            left: row.scrollLeft + delta,
            behavior: REDUCE.matches ? 'auto' : 'smooth'
          });
          mark(i);
        });
      });

      mark(nearest());
    });
  }

  function init(root) {
    var scope = root || document;
    initReveals(scope);
    initTabs(scope);
    initScrollNav(scope);
  }

  init(document);

  /* Theme editor: bind whatever the merchant just added or re-rendered. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
